import React, { useState } from "react";
import { Mail, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { submitInquiry } from "../lib/api";
import { SERVICES } from "../lib/content";

const Overline = ({ children }) => (
  <span className="inline-block font-sans text-[11px] md:text-xs uppercase tracking-[0.28em] text-[#C5A880] font-semibold">
    {children}
  </span>
);

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await submitInquiry(payload);
      setDone(true);
      setForm(initial);
      toast.success("Your inquiry has been received. We'll be in touch within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Overline>Contact</Overline>
            <h1 className="mt-8 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium text-[#0A192F] leading-[0.98]">
              Connect with <br /> <span className="italic">our team.</span>
            </h1>
            <p className="mt-8 font-sans text-lg text-slate-600 leading-relaxed max-w-md">
              Tell us about your business, your goals, and how we can help. A member of our CPA-led
              team will personally respond within one business day with the right solution for your
              needs.
            </p>

            <div className="mt-16 space-y-8">
              <div className="flex items-start gap-4">
                <Mail size={20} strokeWidth={1.5} className="text-[#C5A880] mt-1" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</div>
                  <a href="mailto:erviongroupllc@gmail.com" data-testid="contact-email-link" className="mt-1 block font-serif text-2xl text-[#0A192F] link-gold">
                    erviongroupllc@gmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={20} strokeWidth={1.5} className="text-[#C5A880] mt-1" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Phone</div>
                  <div className="mt-1 font-serif text-2xl text-[#0A192F]">+1 (857) 314-1535</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="border border-[#0A192F] bg-[#F8F9FA] p-12 md:p-16 text-center"
              >
                <CheckCircle2 size={48} strokeWidth={1.25} className="text-[#C5A880] mx-auto" />
                <h2 className="mt-8 font-serif text-4xl text-[#0A192F] tracking-tight">Thank you.</h2>
                <p className="mt-4 font-sans text-slate-600 max-w-md mx-auto">
                  Your inquiry has been logged. A partner from Ervion will personally respond within
                  one business day.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-reset-btn"
                  className="mt-10 inline-flex items-center gap-2 font-sans text-sm tracking-[0.18em] uppercase text-[#0A192F] link-gold"
                >
                  Send another inquiry <ArrowRight size={16} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-[#E2E8F0] bg-white p-8 md:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.22em] text-slate-600">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={update("name")}
                      required
                      className="rounded-none border-0 border-b border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] px-0 h-11 font-serif text-lg"
                      placeholder="Jane Founder"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.22em] text-slate-600">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={update("email")}
                      required
                      className="rounded-none border-0 border-b border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] px-0 h-11 font-serif text-lg"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-xs uppercase tracking-[0.22em] text-slate-600">
                      Company
                    </Label>
                    <Input
                      id="company"
                      data-testid="contact-input-company"
                      value={form.company}
                      onChange={update("company")}
                      className="rounded-none border-0 border-b border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] px-0 h-11 font-serif text-lg"
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.22em] text-slate-600">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className="rounded-none border-0 border-b border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] px-0 h-11 font-serif text-lg"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.22em] text-slate-600">
                    Interested Service
                  </Label>
                  <Select
                    value={form.service}
                    onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                  >
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="rounded-none border-0 border-b border-[#E2E8F0] focus:ring-0 focus:border-[#0A192F] px-0 h-11 font-serif text-lg"
                    >
                      <SelectValue placeholder="Select a service (optional)" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title} className="font-sans text-sm" data-testid={`service-option-${s.id}`}>
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Not sure yet" className="font-sans text-sm italic" data-testid="service-option-unsure">
                        Not sure yet — advise me
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.22em] text-slate-600">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    required
                    className="rounded-none border border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#0A192F] font-sans text-base p-4 resize-none"
                    placeholder="Tell us where your company is today and where you'd like to be."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#0A192F] text-white px-10 py-4 font-sans text-[12px] tracking-[0.22em] uppercase hover:bg-[#1E293B] transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {submitting ? "Submitting…" : "Send Inquiry"}
                  {!submitting && <ArrowRight size={16} strokeWidth={1.5} />}
                </button>

                <p className="text-xs text-slate-500 font-sans">
                  By submitting, you agree to be contacted by a partner from Ervion. We do not
                  sell or share your data.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
